/**
 * `fetch` with the shared request deadline.
 *
 * The fetch-side counterpart of axios's `timeout` option, for the two pre-auth
 * clients that stay on raw `fetch` on purpose: `shared/auth/authApi.ts` (the
 * `/auth/me` bootstrap must not reach the auth/refresh interceptors) and
 * `shared/auth/demoApi.ts` (anonymous public routes called before any session
 * exists). See `docs/platform/error-handling-design.md` § 3.
 *
 * NOTE, until WI 4cd6ed92 lands: `axiosInstance` does NOT yet set a `timeout`,
 * so every authenticated call in the app is still unbounded. That is the sibling
 * item's deliverable, deliberately not duplicated here. Do not read this module
 * as evidence that the axios path is already covered.
 *
 * Implemented with `AbortController` + `setTimeout` rather than
 * `AbortSignal.timeout()`, which needs Safari 16 — the iOS app's deployment
 * target is 15.0, where the missing static method would throw on every call and
 * take sign-in down entirely.
 *
 * The rejection is a `DOMException` named `TimeoutError`; `getErrorMessage`
 * translates that — and the `AbortError` a transport may surface instead — to
 * `errors:TIMEOUT`.
 *
 * Known limit: the deadline covers the round trip up to the response, not the
 * reading of its body. A server that sends headers and then stalls mid-body is
 * a different (and far rarer) failure than the connection-accepted-then-silent
 * one this exists for.
 *
 * The helper owns the request's `AbortSignal` — no caller passes one, and a
 * caller that needs its own cancellation belongs on `axiosInstance`.
 */

import { DEFAULT_REQUEST_TIMEOUT_MS } from './requestTimeout';

export async function fetchWithDeadline(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;

  // The deadline both ABORTS and REJECTS, and the two are not redundant. The
  // abort is what actually cancels an in-flight request on a transport that
  // honours the signal (the browser's own fetch). The rejection is what settles
  // THIS promise, and it is the load-bearing half: on native the global fetch is
  // Capacitor's CapacitorHttp shim, which ignores `signal` entirely — so an
  // abort-only deadline would fire, cancel nothing, and leave the caller hanging
  // exactly as before, on the platform we could not see it happen.
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      // No URL in the message. `getErrorMessage` maps this by NAME to
      // `errors:TIMEOUT` and never reads `.message`, so the URL would buy
      // nothing today — and the moment a caller logs the raw rejection it
      // becomes an endpoint leak out of a pre-auth screen.
      const expiry = new DOMException(
        `Request exceeded the ${timeoutMs}ms deadline`,
        'TimeoutError',
      );
      controller.abort(expiry);
      reject(expiry);
    }, timeoutMs);
  });

  const inFlight = fetch(url, { ...options, signal: controller.signal });
  // The race has already rejected by the time the abort lands, so the losing
  // fetch rejection would otherwise surface as an unhandled rejection.
  inFlight.catch(() => {});

  try {
    return await Promise.race([inFlight, deadline]);
  } finally {
    clearTimeout(timer!);
  }
}
